import type { Layout } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import { stadhuisPlanOf } from '../fabric/landmarks/stadhuis/stadhuisPlan';
import { toLayout } from '../fabric/landmarks/planFrame';
import { restoreWalkNet, passable, type Ground } from './crowd/walkNet';
import type { BakedCrowd } from './crowd/crowdPlan';
import { routeTree } from './crowd/routes';
import { alongLine, type WalkLine, type OnLine } from './crowd/walkLine';

export interface CivicOffice {
  readonly clerk: Vec2;
  readonly facing: number;
  readonly routes: readonly (readonly Vec2[])[];
}
export interface CivicPlan {
  readonly offices: readonly CivicOffice[];
  readonly patrol: readonly Vec2[];
}
export function planCivicActivity(layout: Layout, crowd: BakedCrowd, ground: Ground): CivicPlan {
  const hall = layout.landmarks.find((l) => l.id === 'landmark-oude-stadhuis');
  if (!hall) return { offices: [], patrol: [] };
  const plan = stadhuisPlanOf(hall),
    net = restoreWalkNet(crowd.net);
  const here = (u: number, v: number) => toLayout(plan.frame.plan, [u, v]);
  const clear = (p: Vec2) =>
    [
      [0, 0],
      [0.38, 0],
      [-0.38, 0],
      [0, 0.38],
      [0, -0.38],
    ].every(([dx, dy]) => ground.open([p[0] + dx!, p[1] + dy!]));
  const offices: CivicOffice[] = [];
  for (const arch of [plan.arcade[0]!, plan.arcade.at(-1)!]) {
    const clerk = here(arch.center, 1.7),
      visitor = here(arch.center, -0.15),
      approach = here(arch.center, -2.5);
    if (![clerk, visitor, approach].every(clear) || !passable(visitor, approach, clear)) continue;
    const near = Array.from({ length: net.count }, (_, i) => i)
      .filter((i) => net.half[i]! >= 0.5)
      .sort(
        (a, b) =>
          Math.hypot(net.x[a]! - approach[0], net.y[a]! - approach[1]) -
          Math.hypot(net.x[b]! - approach[0], net.y[b]! - approach[1])
      );
    const attach = near.find((i) => passable([net.x[i]!, net.y[i]!], approach, clear));
    if (attach === undefined) continue;
    const tree = routeTree(net, attach);
    const starts = near.filter((i) => tree.distance[i]! >= 22 && tree.distance[i]! <= 70);
    const routes: Vec2[][] = [];
    for (const start of starts) {
      const path: Vec2[] = [];
      for (let at = start; at >= 0; at = tree.next[at]!) path.push([net.x[at]!, net.y[at]!]);
      path.push(approach, visitor);
      if (!path.every(clear) || !path.slice(1).every((p, i) => passable(path[i]!, p, clear)))
        continue;
      if (routes.some((r) => Math.hypot(r[0]![0] - path[0]![0], r[0]![1] - path[0]![1]) < 5))
        continue;
      routes.push(path);
      if (routes.length === 6) break;
    }
    if (routes.length)
      offices.push({ clerk, facing: plan.frame.plan.heading - Math.PI / 2, routes });
  }
  const patrol = [here(1.3, 2.5), here(plan.vierschaar.u1 - 1.3, 2.5)];
  return { offices, patrol: passable(patrol[0]!, patrol[1]!, clear) ? patrol : [] };
}

/** Arrival, a short attended exchange, departure; the same stride-distance drives the feet. */
export function civicVisitorAt(
  line: WalkLine,
  seconds: number,
  phase: number,
  speed: number,
  pause = 6
) {
  const travel = line.length / speed,
    cycle = travel * 2 + pause;
  const t = (((seconds + phase * cycle) % cycle) + cycle) % cycle;
  const returning = t > travel + pause,
    serving = t >= travel && !returning;
  const distance = serving
    ? line.length
    : returning
      ? line.length - (t - travel - pause) * speed
      : t * speed;
  const at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
  alongLine(line, distance / line.length, at);
  if (returning) at.heading += Math.PI;
  return {
    ...at,
    serving,
    moving: !serving,
    walked: returning ? line.length + (line.length - distance) : distance,
  };
}
